import { API } from "../../shared/apiRoutes";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { AddCategoryRes, BulkDeleteResponse, CategoryDataResponse, CategoryListProps, CategoryProductItem, CategoryProductRes, CategoryProductsPayloadData, CategoryProductsProps, DeleteCategoryRes, DuplicateJsonType, EditCategoryRes, ParentCategoryDataRes, PayloadProps, PluginRes, SaveCategory, SaveCategoryRes, SelectedItemProps, SortCategoryDataResponse, UpdateCategoryRes } from "../../components/category/categoryModule";

interface initialData {
    loading: boolean,
    categoryDataValue: { value: CategoryListProps[], count: number, loading: boolean }
    error: string,
    categoryDeleteResponse: DeleteCategoryRes | null,
    categoryBulkDeleteResponse: BulkDeleteResponse | null,
    categoryProductResponse: { value: CategoryProductItem[], count: number, loading: boolean },
    getOneCategory: { value: EditCategoryRes | null, loading: boolean, error: string }
    getParentCategory: ParentCategoryDataRes | null,
    addCategory: { value: AddCategoryRes | null, loading: boolean, error: string }
    updateCategory: { value: UpdateCategoryRes | null, loading: boolean, error: string },
    sortCategory: SortCategoryDataResponse | null,
    getOnePlugin: { value: PluginRes | null, loading: boolean, error: string },
    addEditCategoryLoader: boolean
}
interface categoryDeleteProps {
    storeId: number,
    id: number
}
interface categoryProductDeleteProps {
    storeId: number,
    productId: number[],
    categoryId: number
}

interface categoryDuplicatePayload {
    value: String,
    columnName: String,
    tableName: String,
    storeId: boolean,
    id: number | null
}
interface categryBulkDeleteProps {
    storeId: number,
    categoryArr: number[]
}

interface PayloadDataProps {
    storeId: number,
    data?: PayloadProps | undefined
}

interface addCategoryProps {
    storeId: number,
    data: SaveCategory | undefined
}

export interface CategoryProductPayload {
    storeId: number,
    data: CategoryProductsPayloadData | undefined,
    isAllProduct: boolean
}

//initial state
const initialState: initialData = {
    loading: false,
    categoryDataValue: { value: [], count: 0, loading: false },
    error: '',
    categoryDeleteResponse: null,
    categoryBulkDeleteResponse: null,
    categoryProductResponse: { value: [], count: 0, loading: false },
    getOneCategory: { value: null, loading: true, error: "" },
    getParentCategory: null,
    addCategory: { value: null, loading: true, error: "" },
    updateCategory: { value: null, loading: true, error: "" },
    sortCategory: null,
    getOnePlugin: { value: null, loading: true, error: "" },
    addEditCategoryLoader: false
}

//getAllCategoryList call *
export const getAllPosCategoryData = createAsyncThunk('category/getAllPosCategoryData', ({ storeId, data }: PayloadDataProps) => {
    const url = HttpRoutingService.replaceUrl(API.getAllPosCategories, { storeId: storeId })
    return HttpRoutingService.getMethod<CategoryDataResponse>(url, data ?? {})
})
//single delete category data *
export const deleteCategoryData = createAsyncThunk('category/deleteCategoryData', ({ storeId, id }: categoryDeleteProps) => {
    const url = HttpRoutingService.replaceUrl(API.deleteCategory, { storeId: storeId, categoryId: id })
    return HttpRoutingService.deleteMethod<DeleteCategoryRes>(url)
})
//bulk delete category list *
export const bulkDeleteCategoryData = createAsyncThunk('category/bulkDeleteCategoryData', ({ storeId, categoryArr }: categryBulkDeleteProps) => {
    const url = HttpRoutingService.replaceUrl(API.bulkDeleteCategory, { storeId: storeId })
    return HttpRoutingService.putMethod<BulkDeleteResponse>(url, { categoryArray: categoryArr })
})
//getMappedCategoryProducts *
export const getMappedCategoryProducts = createAsyncThunk('category/getMappedCategoryProducts', ({ storeId: storeId, data, isAllProduct }: CategoryProductPayload) => {
    const url = HttpRoutingService.replaceUrl(API.getAllPosCategoryProducts, { storeId: storeId })
    return HttpRoutingService.getMethod<CategoryProductRes>(url, data)
})
//getOneCategory details *
export const getOnePosCategories = createAsyncThunk("category/getOnePosCategories", ({ storeId, id }: { storeId: number, id: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOneCategory, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<EditCategoryRes>(url)
})
//getParentCategory *
export const getPosParentCategory = createAsyncThunk("category/getPosParentCategory", ({ storeId }: { storeId: number }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getParentCategory, { storeId: storeId })
    return HttpRoutingService?.getMethod<ParentCategoryDataRes>(url)
})
//createCategory *
export const addPosCategory = createAsyncThunk("category/addPosCategory", ({ storeId, data }: addCategoryProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.addCategory, { storeId: storeId })
    return HttpRoutingService?.postMethod<AddCategoryRes>(url, data, null)
})
//updatePosCategory *
export const updatePosCategory = createAsyncThunk("category/updatePosCategory", ({ storeId, editId, data }: { storeId: number, editId: number, data: SaveCategory }) => {
    const url = HttpRoutingService.replaceUrl(API.updateCategory, { storeId: storeId, editId: editId })
    return HttpRoutingService.putMethod(url, data)
})
//getPosSortCategoryList
export const getPosSortCategoryList = createAsyncThunk("category/getPosSortCategoryList", ({ storeId, data }: { storeId: number, data: { sort: boolean, isProductPage: boolean } }) => {
    const url = HttpRoutingService.replaceUrl(API.getSortCategoryList, { storeId: storeId })
    return HttpRoutingService.getMethod<SortCategoryDataResponse>(url, data)
})
//getOnePosPlugin *
export const getOnePosPlugin = createAsyncThunk("category/getOnePosPlugin", ({ storeId, data }: { storeId: number, data: { pluginCode: string } }) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePlugin, { storeId: storeId })
    return HttpRoutingService.getMethod<PluginRes>(url, data)
})
//deleteCategoryProduct *
export const deleteCategoryProduct = createAsyncThunk('category/deleteCategoryProduct', ({ storeId, categoryId, productId }: categoryProductDeleteProps) => {
    const url = HttpRoutingService.replaceUrl(API.deleteCategoryProduct, { storeId: storeId, productID: JSON.stringify(productId), id: categoryId })
    return HttpRoutingService.deleteMethod<DeleteCategoryRes>(url)
})

//checkDuplicateCategory *
export const getOnePostDuplicateCheck = createAsyncThunk('category/getOnePostDuplicateCheck', ({ storeId, data }: { storeId: number, data: categoryDuplicatePayload }) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePostDuplicateCheck, { storeId: storeId })
    return HttpRoutingService.postMethod<DuplicateJsonType>(url, data)
})

export const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {},

    extraReducers(builder) {
        builder
            .addCase(getAllPosCategoryData.pending, (state) => {
                state.categoryDataValue.loading = true
            })
            .addCase(getAllPosCategoryData.fulfilled, (state, action) => {

                const offset = action.meta.arg.data?.offset || 0
                const rows = action.payload?.categories?.rows || []
                if (offset > 0) {
                    state.categoryDataValue.value = [...state.categoryDataValue.value, ...rows]

                } else {
                    state.categoryDataValue.value = rows;
                }
                state.categoryDataValue.count = action?.payload?.categories?.count || 0;
                state.categoryDataValue.loading = false;
            })
            .addCase(getAllPosCategoryData.rejected, (state) => {
                state.categoryDataValue.loading = false
                state.error = 'Error in getCategoryList'
            })
            .addCase(deleteCategoryData.pending, (state) => {
                state.loading = true
            })
            .addCase(deleteCategoryData.fulfilled, (state, action) => {
                state.loading = false
                state.categoryDeleteResponse = action.payload
            })
            .addCase(deleteCategoryData.rejected, (state) => {
                state.loading = false
                state.error = 'Failed to delete category'
            })
            .addCase(bulkDeleteCategoryData.pending, (state) => {
                state.loading = true
            })
            .addCase(bulkDeleteCategoryData.fulfilled, (state, action) => {
                state.loading = false
                state.categoryBulkDeleteResponse = action.payload
            })
            .addCase(bulkDeleteCategoryData.rejected, (state) => {
                state.loading = false
                state.error = 'Failed to delete category'
            })
            .addCase(getMappedCategoryProducts.pending, (state) => {
                state.categoryProductResponse.loading = true
            })
            .addCase(getMappedCategoryProducts.fulfilled, (state, action) => {
                const rows = action.payload?.products?.rows || []
                const offset = action?.meta?.arg?.data?.offset || 0
                if (offset > 0) {
                    state.categoryProductResponse.value = [...state.categoryProductResponse.value, ...rows]
                } else {
                    state.categoryProductResponse.value = rows;
                }
                state.categoryProductResponse.count = action?.payload?.products?.count || 0;

                state.categoryProductResponse.loading = false;
            })

            .addCase(getMappedCategoryProducts.rejected, (state) => {
                state.categoryProductResponse.loading = false
                state.error = 'Error in get SelectedCategoryProduct list'
            })
            .addCase(getOnePosCategories?.pending, (state) => {
                state.getOneCategory.loading = true
            })
            .addCase(getOnePosCategories?.fulfilled, (state, action) => {
                state.getOneCategory.loading = false
                state.getOneCategory.value = action?.payload
            })
            .addCase(getOnePosCategories?.rejected, (state, action) => {
                state.getOneCategory.loading = false
                state.getOneCategory.error = action?.error?.message ? action?.error?.message : "Err in getOneCategory"
            })
            .addCase(getPosParentCategory?.pending, (state) => {
                state.loading = true
            })
            .addCase(getPosParentCategory?.fulfilled, (state, action) => {
                state.loading = false
                state.getParentCategory = JSON.parse(JSON.stringify(action.payload));
            })
            .addCase(getPosParentCategory?.rejected, (state, action) => {
                state.loading = false
                state.error = action?.error?.message ? action?.error?.message : "Err in getParentCategory"
            })
            .addCase(getPosSortCategoryList?.pending, (state) => {
                state.addCategory.loading = true
            })
            .addCase(getPosSortCategoryList?.fulfilled, (state, action) => {
                state.addCategory.loading = false
                state.sortCategory = action.payload
            })
            .addCase(getPosSortCategoryList?.rejected, (state, action) => {
                state.addCategory.loading = false
                state.addCategory.error = action?.error?.message ? action?.error?.message : "Error in addCategory"
            })
            .addCase(getOnePosPlugin?.pending, (state) => {
                state.getOnePlugin.loading = true
            })
            .addCase(getOnePosPlugin?.fulfilled, (state, action) => {
                state.getOnePlugin.loading = false
                state.getOnePlugin.value = action.payload
            })
            .addCase(getOnePosPlugin?.rejected, (state, action) => {
                state.getOnePlugin.loading = false
                state.getOnePlugin.error = action?.error?.message ? action?.error?.message : "Error in getOnePlugin"
            })

            //addCategory
            .addCase(addPosCategory?.pending, (state) => {
                state.addEditCategoryLoader = true
            })
            .addCase(addPosCategory?.fulfilled, (state) => {
                state.addEditCategoryLoader = false
            })
            .addCase(addPosCategory?.rejected, (state) => {
                state.addEditCategoryLoader = false
            })
            //EditCategory
            .addCase(updatePosCategory?.pending, (state) => {
                state.addEditCategoryLoader = true
            })
            .addCase(updatePosCategory?.fulfilled, (state) => {
                state.addEditCategoryLoader = false
            })
            .addCase(updatePosCategory?.rejected, (state) => {
                state.addEditCategoryLoader = false
            })
    }
})
export default categorySlice.reducer